import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useInterfacePrefsQuery, useUpdateInterfacePrefs, type Theme } from '@boltcrm/platform-web';

export type { Theme };
const KEY = 'boltcrm.theme';
const listeners = new Set<() => void>();

/** The theme this browser last used, or `null` if none was ever picked here. */
function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  } catch {
    return null;
  }
}

/**
 * The theme shown before sign-in and until the user's own setting loads: this browser's last one, else Light
 * (Pipedrive's default, settings-general.md).
 */
export function getTheme(): Theme {
  return storedTheme() ?? 'light';
}

const STYLE_KEY = 'boltcrm.style';
export type Style = 'pipedrive' | 'promax';

/** The look this browser uses: Pipedrive-exact unless someone picked Pro Max here (ADR-955). */
export function getStyle(): Style {
  try {
    return localStorage.getItem(STYLE_KEY) === 'promax' ? 'promax' : 'pipedrive';
  } catch {
    return 'pipedrive';
  }
}

export function applyStyle(style: Style = getStyle()) {
  if (style === 'promax') document.documentElement.dataset.style = 'promax';
  else delete document.documentElement.dataset.style;
  try {
    localStorage.setItem(STYLE_KEY, style);
  } catch {
    /* storage may be unavailable */
  }
  listeners.forEach((l) => l());
}

/** The chosen style and its setter; it applies at once and lives in this browser only. */
export function useStyle(): [Style, (style: Style) => void] {
  return [useSyncExternalStore(subscribe, getStyle), applyStyle];
}

const darkQuery = () => matchMedia('(prefers-color-scheme: dark)');

/**
 * Applies Light / Dark / System (Interface preferences → Appearance → Theme) and remembers it in this browser.
 * Start-up passes `remember: false`, so a browser where no one picked a theme stays unmarked.
 */
export function applyTheme(theme: Theme = getTheme(), { remember = true }: { remember?: boolean } = {}) {
  const dark = theme === 'dark' || (theme === 'system' && darkQuery().matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  if (remember) {
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* storage may be unavailable */
    }
  }
  listeners.forEach((l) => l());
}

/** With System selected, follows the OS when it switches between light and dark. Call once at start-up. */
export function followSystemTheme() {
  darkQuery().addEventListener('change', () => {
    if (getTheme() === 'system') applyTheme('system', { remember: false });
  });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The chosen theme and its setter, which applies it at once and saves it to the user's preferences (ADR-143). */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, getTheme);
  const { mutate } = useUpdateInterfacePrefs();
  const set = useCallback(
    (next: Theme) => {
      applyTheme(next);
      mutate({ theme: next });
    },
    [mutate],
  );
  return [theme, set];
}

/**
 * Keeps the browser on the signed-in user's theme (ADR-143): once their preferences load, their saved theme
 * applies, on any browser. Someone who never picked one keeps this browser's last choice, copied to their
 * preferences once (themes picked before they were stored per user).
 */
export function useThemeSync() {
  const { data } = useInterfacePrefsQuery();
  const { mutate } = useUpdateInterfacePrefs();
  const saved = data?.theme;
  const loaded = !!data;
  useEffect(() => {
    if (!loaded) return;
    if (saved) {
      if (saved !== getTheme()) applyTheme(saved);
      return;
    }
    const local = storedTheme();
    if (local) mutate({ theme: local });
  }, [loaded, saved, mutate]);
}
